import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import type { LucideIcon } from "lucide-react";
import { getStepDisplay, type TipState } from "@/types/tips";
import { toRelativeCtaPath, type NextStepResponse } from "@/services/nextStep";
import { useServerNextStep } from "@/hooks/useNextStep";
import { URL_PARSE_BASE } from "@/constants/domains";

const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function getStorageKey(userId: string) {
  return `recruit_ai_tips:${userId}`;
}

function loadTipState(userId: string): TipState {
  try {
    const raw = localStorage.getItem(getStorageKey(userId));
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        dismissed: parsed.dismissed || {},
        impressions: parsed.impressions || {},
      };
    }
  } catch {}
  return { dismissed: {}, impressions: {} };
}

function saveTipState(userId: string, state: TipState) {
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(state));
  } catch {}
}

interface UseFeatureTipsProps {
  userId: string | null | undefined;
}

export interface FeatureTip {
  id: string; // server stepKey
  title: string;
  description: string;
  icon: LucideIcon;
  action: { type: "prompt"; prompt: string } | { type: "navigate"; path: string };
}

/** Map a server next step onto the FeatureTip shape the UI renders. */
function buildTipFromStep(step: NextStepResponse): FeatureTip {
  const display = getStepDisplay(step.stepKey, step.ctaLabel);

  let action: FeatureTip["action"] = { type: "navigate", path: toRelativeCtaPath(step.ctaUrl) };
  try {
    const url = new URL(step.ctaUrl, URL_PARSE_BASE);
    const prompt = url.searchParams.get("prompt");
    // A /chat?prompt= CTA means "send this prompt to the coach" — surface it
    // as a prompt action so the chat page can send it directly.
    if (url.pathname === "/chat" && prompt) {
      action = { type: "prompt", prompt };
    }
  } catch {}

  return {
    id: step.stepKey,
    title: display.title,
    description: display.description,
    icon: display.icon,
    action,
  };
}

/**
 * Server-driven feature tips. The server decides the next best action
 * (GET /users/me/next-step); this hook only layers on client UI
 * bookkeeping: 7-day dismissals and impression counts (localStorage).
 * If the server fetch fails, all tip surfaces stay hidden.
 */
export function useFeatureTips({ userId }: UseFeatureTipsProps) {
  const serverStep = useServerNextStep(!!userId);
  const [tipState, setTipState] = useState<TipState>(() =>
    userId ? loadTipState(userId) : { dismissed: {}, impressions: {} }
  );
  const lastShownTipRef = useRef<string | null>(null);

  // Reload state when userId changes
  useEffect(() => {
    if (!userId) return;
    setTipState(loadTipState(userId));
  }, [userId]);

  const currentTip: FeatureTip | null = useMemo(() => {
    if (!userId || !serverStep) return null;

    const dismissedAt = tipState.dismissed[serverStep.stepKey];
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_DURATION_MS) return null;

    return buildTipFromStep(serverStep);
  }, [userId, serverStep, tipState.dismissed]);

  // Record impression when the shown tip changes
  useEffect(() => {
    if (!currentTip || !userId) return;
    if (currentTip.id === lastShownTipRef.current) return;
    lastShownTipRef.current = currentTip.id;
    setTipState(prev => {
      const next = {
        ...prev,
        impressions: {
          ...prev.impressions,
          [currentTip.id]: (prev.impressions[currentTip.id] || 0) + 1,
        },
      };
      saveTipState(userId, next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTip?.id, userId]);

  const dismissTip = useCallback(
    (tipId: string) => {
      if (!userId) return;
      setTipState(prev => {
        const next = {
          ...prev,
          dismissed: { ...prev.dismissed, [tipId]: Date.now() },
        };
        saveTipState(userId, next);
        return next;
      });
    },
    [userId]
  );

  // Discovery tips for WelcomeMessage — same server source as the tip strip
  const discoveryTips: FeatureTip[] = useMemo(() => (currentTip ? [currentTip] : []), [currentTip]);

  return {
    currentTip,
    discoveryTips,
    dismissTip,
  };
}
